import React from 'react';
import { EditIcon3, DeleteIcon } from 'assets';
import { JobCriteriaData } from '../types';
import { TechnicalSkillsEditModal, DeleteSectionModal } from 'components';
import { useTechnicalSkillsEdit, useTechnicalSkillsDelete, useToast } from 'hooks';
import { TechnicalSkillsPayload } from 'hooks';
import isEqual from 'lodash/isEqual';
import { toTitleCase } from 'utils/titleCase';

interface TechnicalSkillsSectionProps {
  assessmentId: string | null;
  data: JobCriteriaData['technicalSkills'];
  onEdit: () => void;
  onDelete: () => void;
  onSave: (updatedData: JobCriteriaData['technicalSkills']) => void;
  disabled?: boolean;
  /** Section title. Defaults to "Technical Skills". */
  heading?: string;
}

const initialData = {
  technicalSkills: []
};

const TechnicalSkillsSection: React.FC<TechnicalSkillsSectionProps> = ({
  assessmentId,
  data,
  onEdit,
  onDelete,
  onSave,
  disabled,
  heading = 'Technical Skills'
}) => {
  const { openToast } = useToast();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  const {
    submitTechnicalSkills,
    isSubmitting
  } = useTechnicalSkillsEdit(assessmentId);

  const {
    deleteTechnicalSkills,
  } = useTechnicalSkillsDelete(assessmentId);

  const onSubmit = (formData: JobCriteriaData['technicalSkills']) => {
    if (!assessmentId || isSubmitting) {
      openToast({
        show: true,
        heading: "Required",
        text: "Assessment ID is required",
        type: "error",
        timeOut: 5000,
      });
      return;
    }

    const payload: TechnicalSkillsPayload = {
      assessment_id: assessmentId,
      ...formData.technicalSkills.reduce((acc, skill, index) => ({
        ...acc,
        [`skill${index + 1}`]: skill.title,
        [`expectation${index + 1}`]: skill.expectation
      }), {})
    };

    submitTechnicalSkills(payload, {
      onSuccess: () => {
        onSave(formData);
        setIsModalOpen(false);
      },
      onError: (err) => {
        setIsModalOpen(false);
      },
    });
  };

  const handleDeleteConfirm = () => {
    if (!assessmentId) {
      openToast({
        show: true,
        heading: "Required",
        text: "Assessment ID is required for deletion",
        type: "info",
        timeOut: 5000,
      });
      return;
    }

    deleteTechnicalSkills({ assessment_id: assessmentId }, {
      onSuccess: () => {
        onDelete();
        setIsDeleteModalOpen(false);
      },
    });
  };

  if (isEqual(data, initialData)) return null;
  if (!data) return null;

  return (
    <section className="border-t border-[#dfe1e6] pt-6 mb-8 relative">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl sm:text-2xl font-medium sm:font-semibold m-0">{heading}</h2>
        {!disabled && (
          <div className="flex gap-3.5 items-center">
            <span className="cursor-pointer">
              <EditIcon3 onClick={() => { onEdit(); setIsModalOpen(true); }} />
            </span>
            <div className="relative group cursor-pointer">
              <DeleteIcon onClick={() => setIsDeleteModalOpen(true)} />
              <div className="absolute bottom-full right-0 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-52 z-10">
                <span className="block bg-[#1d2939] text-white text-xs rounded-lg px-3.5 py-2.5 leading-relaxed">
                  You can delete section you don't want to screen at this stage
                </span>
                <span className="absolute top-full right-[18px] w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-[#1d2939]" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        {data.technicalSkills.map((skill, index) => (
          <div key={index} className="flex flex-col">
            <div className="text-base font-semibold text-[#1D2939]">{toTitleCase(skill.title)}</div>
            <div className="pl-3 mb-3.5">
              <span className="text-[#1D2939] text-sm mr-1">●</span>
              <span className="font-semibold mr-1 text-[#1D2939]">Expectation:</span>
              <span className="font-normal text-[#1D2939]">{skill.expectation}</span>
            </div>
          </div>
        ))}
      </div>

      <TechnicalSkillsEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        currentData={data}
        onSave={onSubmit}
        isSubmitting={isSubmitting}
      />

      <DeleteSectionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        sectionName="Technical Skills"
      />
    </section>
  );
};

export default TechnicalSkillsSection;
